import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeftIcon,
  ArrowRightIcon,
  PlusIcon,
  TrashIcon,
  EyeIcon,
  StarIcon
} from '@heroicons/react/24/outline';
import { getFirestore, collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { format } from 'date-fns';
import { partnerService } from '../firebase/services';

const PartnerDetail = () => {
  const { partnerId } = useParams();
  const navigate = useNavigate();
  const [partner, setPartner] = useState(null);
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage] = useState(10);
  const [allPartners, setAllPartners] = useState([]);
  const [currentPartnerIndex, setCurrentPartnerIndex] = useState(-1);

  // Load all partners for navigation
  useEffect(() => {
    const loadAllPartners = async () => {
      try {
        const partners = await partnerService.getAllPartners();
        setAllPartners(partners);
        
        // Find current partner index
        const index = partners.findIndex(p => p.id === partnerId);
        setCurrentPartnerIndex(index);
      } catch (err) {
        console.error('Error loading all partners:', err);
      }
    };

    loadAllPartners();
  }, [partnerId]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'ArrowLeft' && currentPartnerIndex > 0) {
        handlePrev();
      } else if (event.key === 'ArrowRight' && currentPartnerIndex < allPartners.length - 1) {
        handleNext();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [currentPartnerIndex, allPartners.length]);

  // Load partner data from Firebase
  useEffect(() => {
    const loadPartnerData = async () => {
      try {
        setLoading(true);
        const db = getFirestore();
        
        // Get partner data
        const partnerDoc = await getDoc(doc(db, 'users', partnerId));
        if (!partnerDoc.exists()) {
          throw new Error('Partner not found');
        }
        
        const partnerData = {
          id: partnerDoc.id,
          ...partnerDoc.data()
        };
        setPartner(partnerData);
        
        // Get deals for this partner
        try {
          // Remove +91 prefix from phone number for comparison
          const phoneWithoutPrefix = partnerData.phone?.replace('+91', '') || '';
          
          const dealsQuery = query(
            collection(db, 'deals'),
            where('dealProviderPhone', '==', phoneWithoutPrefix)
          );
          const dealsSnapshot = await getDocs(dealsQuery);
          const dealsData = dealsSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          setDeals(dealsData);
        } catch (err) {
          console.log('No deals found or error loading deals:', err);
          setDeals([]);
        }
        
        setError(null);
      } catch (err) {
        setError('Failed to load partner details');
        console.error('Error loading partner data:', err);
      } finally {
        setLoading(false);
      }
    };

    if (partnerId) {
      loadPartnerData();
    }
  }, [partnerId]);

  const handleRemovePartner = async () => {
    if (window.confirm('Are you sure you want to remove this partner?')) {
      try {
        // Add partner deletion logic here
        alert('Partner removed successfully!');
        navigate('/partners-list');
      } catch (error) {
        console.error('Error removing partner:', error);
        alert('Failed to remove partner');
      }
    }
  };

  const handleAddNewDeal = () => {
    // Add new deal logic will be implemented when connecting to backend
    console.log('Adding new deal for partner:', partnerId);
  };

  const handlePrev = () => {
    if (currentPartnerIndex > 0) {
      const prevPartner = allPartners[currentPartnerIndex - 1];
      navigate(`/partner-detail/${prevPartner.id}`);
    }
  };

  const handleNext = () => {
    if (currentPartnerIndex < allPartners.length - 1) {
      const nextPartner = allPartners[currentPartnerIndex + 1];
      navigate(`/partner-detail/${nextPartner.id}`);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return format(date, 'MMM dd, yyyy');
    } catch (error) {
      return 'N/A';
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return '₹0.00';
    return `₹${parseFloat(amount).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
  };

  // Pagination for deals
  const totalPages = Math.ceil(deals.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const currentDeals = deals.slice(startIndex, endIndex);

  const handlePageChange = (newPage) => {
    setCurrentPage(Math.max(1, Math.min(newPage, totalPages)));
  };

  if (loading) {
    return (
      <div className="h-full overflow-y-auto p-6 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Loading partner details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !partner) {
    return (
      <div className="h-full overflow-y-auto p-6 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">{error || 'Partner not found'}</p>
            <button
              onClick={() => navigate('/partners-list')}
              className="mt-2 text-blue-600 hover:text-blue-800"
            >
              ← Back to Partners
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto p-6 bg-gray-50">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/partners-list')}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <ArrowLeftIcon className="h-5 w-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Partner Detail</h1>
              {allPartners.length > 0 && currentPartnerIndex >= 0 && (
                <p className="text-sm text-gray-500 mt-1">
                  Partner {currentPartnerIndex + 1} of {allPartners.length}
                  <span className="ml-2 text-xs text-gray-400">
                    (Use ← → arrow keys to navigate)
                  </span>
                </p>
              )}
            </div>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={handleAddNewDeal}
              className="btn-primary flex items-center"
            >
              <PlusIcon className="w-4 h-4 mr-2" />
              ADD NEW DEAL
            </button>
            <button
              onClick={handlePrev}
              disabled={currentPartnerIndex <= 0 || loading}
              className={`btn-primary flex items-center ${
                currentPartnerIndex <= 0 || loading ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              <ArrowLeftIcon className="w-4 h-4 mr-2" />
              PREV
            </button>
            <button
              onClick={handleNext}
              disabled={currentPartnerIndex >= allPartners.length - 1 || loading}
              className={`btn-primary flex items-center ${
                currentPartnerIndex >= allPartners.length - 1 || loading ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              NEXT
              <ArrowRightIcon className="w-4 h-4 ml-2" />
            </button>
          </div>
        </div>

        {/* Partner Information */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Partner Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-500">Operator Number</label>
              <p className="mt-1 text-sm text-gray-900 font-bold text-blue-600">{partner.optnum || 'N/A'}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500">First Name</label>
              <p className="mt-1 text-sm text-gray-900">{partner.firstname || 'N/A'}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500">Last Name</label>
              <p className="mt-1 text-sm text-gray-900">{partner.lastname || 'N/A'}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500">Email</label>
              <p className="mt-1 text-sm text-gray-900">{partner.email || 'N/A'}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500">Phone Number</label>
              <p className="mt-1 text-sm text-gray-900">{partner.phone || 'N/A'}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500">Type</label>
              <p className="mt-1 text-sm text-gray-900">{partner.type || 'N/A'}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500">City</label>
              <p className="mt-1 text-sm text-gray-900">{partner.city || 'N/A'}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500">State</label>
              <p className="mt-1 text-sm text-gray-900">{partner.state || 'N/A'}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500">Membership From</label>
              <p className="mt-1 text-sm text-gray-900">{formatDate(partner.created)}</p>
            </div>
          </div>
        </div>

        {/* Deals Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Deals Listed</h2>
            <p className="text-sm text-gray-500 mt-1">
              {deals.length} deals found
            </p>
          </div>

          {deals.length > 0 ? (
            <>
              <div className="p-6 space-y-4">
                {currentDeals.map((deal) => (
                  <div key={deal.id} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                    {/* Header with Title and Category */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {deal.dealTitle || 'N/A'}
                        </h3>
                        {deal.featuredDealFlag === true && (
                          <StarIcon className="h-5 w-5 text-yellow-500" title="Featured Deal" />
                        )}
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="text-xs text-gray-400 font-mono">
                          ID: {deal.dealID || 'N/A'}
                        </span>
                        <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                          {deal.dealCategory || 'N/A'}
                        </span>
                      </div>
                    </div>

                    {/* Description if available */}
                    {deal.dealDescription && (
                      <div className="mb-3 text-sm text-gray-600">
                        {deal.dealDescription}
                      </div>
                    )}

                    {/* Details Grid */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">Retail Price:</span>
                        <div className="font-medium text-gray-900">{formatCurrency(deal.dealRetailPrice)}</div>
                      </div>
                      <div>
                        <span className="text-gray-500">Discount:</span>
                        <div className="font-medium text-gray-900">{deal.dealOfferedDiscount ? `${deal.dealOfferedDiscount}%` : 'N/A'}</div>
                      </div>
                      <div>
                        <span className="text-gray-500">Final Price:</span>
                        <div className="font-medium text-gray-900">{formatCurrency(deal.dealDiscountedPrice)}</div>
                      </div>
                      <div>
                        <span className="text-gray-500">Net Margin:</span>
                        <div className="font-medium text-gray-900">{formatCurrency(deal.dealMarginNet)}</div>
                      </div>
                      <div>
                        <span className="text-gray-500">Forward Amount:</span>
                        <div className="font-medium text-gray-900">{formatCurrency(deal.dealForwardAmount)}</div>
                      </div>
                      <div>
                        <span className="text-gray-500">Markup %:</span>
                        <div className="font-medium">
                          {deal.dealPostMarkupPercent > 0 ? (
                            <span className="text-blue-600">
                              Post: {deal.dealPostMarkupPercent}%
                            </span>
                          ) : deal.dealPreMarkupPercent > 0 ? (
                            <span className="text-green-600">
                              Pre: {deal.dealPreMarkupPercent}%
                            </span>
                          ) : (
                            'N/A'
                          )}
                        </div>
                      </div>
                      <div>
                        <span className="text-gray-500">Status:</span>
                        <div className="mt-1">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            deal.dealActiveStatusFlag === true 
                              ? 'bg-green-100 text-green-800'
                              : deal.dealActiveStatusFlag === false
                              ? 'bg-red-100 text-red-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {deal.dealActiveStatusFlag === true ? 'Active' : deal.dealActiveStatusFlag === false ? 'Inactive' : 'Unknown'}
                          </span>
                        </div>
                      </div>
                      <div>
                        <span className="text-gray-500">Created:</span>
                        <div className="font-medium text-gray-900">{formatDate(deal.createdAt || deal.created_at)}</div>
                      </div>
                      <div>
                        <span className="text-gray-500">Created At:</span>
                        <div className="font-medium text-gray-900">
                          {deal.createdAt ? 
                            (deal.createdAt.toDate ? 
                              deal.createdAt.toDate().toLocaleString('en-IN', {
                                timeZone: 'Asia/Kolkata',
                                day: '2-digit',
                                month: 'short',
                                year: '2-digit',
                                hour: '2-digit',
                                minute: '2-digit',
                                second: '2-digit',
                                hour12: false
                              }) : 
                              deal.createdAt.toString()
                            ) : 
                            deal.created_at ? 
                              (deal.created_at.toDate ? 
                                deal.created_at.toDate().toLocaleString('en-IN', {
                                  timeZone: 'Asia/Kolkata',
                                  day: '2-digit',
                                  month: 'short',
                                  year: '2-digit',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                  second: '2-digit',
                                  hour12: false
                                }) : 
                                deal.created_at.toString()
                              ) : 
                              'N/A'
                          }
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                  <div className="flex-1 flex justify-between sm:hidden">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700">
                        Showing <span className="font-medium">{startIndex + 1}</span> to{' '}
                        <span className="font-medium">{Math.min(endIndex, deals.length)}</span> of{' '}
                        <span className="font-medium">{deals.length}</span> results
                      </p>
                    </div>
                    <div>
                      <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                        <button
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                          className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <ArrowLeftIcon className="h-5 w-5" />
                        </button>
                        
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                          const pageNum = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
                          if (pageNum > totalPages) return null;
                          
                          return (
                            <button
                              key={pageNum}
                              onClick={() => handlePageChange(pageNum)}
                              className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                pageNum === currentPage
                                  ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                                  : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                              }`}
                            >
                              {pageNum}
                            </button>
                          );
                        })}
                        
                        <button
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === totalPages}
                          className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <ArrowRightIcon className="h-5 w-5" />
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="px-6 py-12 text-center">
              <p className="text-gray-500">No deals found for this partner.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PartnerDetail; 